import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "@phosphor-icons/react";

const N_BARS = 64;

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

async function computePeaks(src) {
  const resp = await fetch(src);
  const buf = await resp.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const audio = await ctx.decodeAudioData(buf);
  const ch = audio.getChannelData(0);
  const block = Math.floor(ch.length / N_BARS) || 1;
  const peaks = [];
  for (let i = 0; i < N_BARS; i++) {
    let max = 0;
    const start = i * block;
    for (let j = 0; j < block; j++) {
      const v = Math.abs(ch[start + j] || 0);
      if (v > max) max = v;
    }
    peaks.push(max);
  }
  ctx.close();
  const norm = Math.max(...peaks) || 1;
  return peaks.map((p) => p / norm);
}

export default function AudioPlayer({ src, label, accent = "#FF5500", testid }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [peaks, setPeaks] = useState(null);

  useEffect(() => {
    let alive = true;
    setPeaks(null);
    if (src) computePeaks(src).then((p) => { if (alive) setPeaks(p); }).catch(() => { if (alive) setPeaks([]); });
    return () => { alive = false; };
  }, [src]);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const a = ref.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const p = (e.clientX - rect.left) / rect.width;
    if (a && dur) a.currentTime = p * dur;
  };

  const pct = dur ? (cur / dur) * 100 : 0;
  const bars = peaks && peaks.length ? peaks : Array.from({ length: N_BARS }, () => 0.05);
  const decoding = peaks === null;

  return (
    <div className="bg-[#18181B] border border-border p-4" data-testid={testid}>
      {label && <div className="overline mb-3">{label}</div>}
      <audio ref={ref} src={src} preload="metadata" />
      <div className="flex items-center gap-4">
        <button onClick={toggle} data-testid={testid ? `${testid}-toggle` : undefined}
          className="h-11 w-11 shrink-0 flex items-center justify-center border transition-transform hover:-translate-y-[1px]"
          style={{ background: accent, borderColor: accent }}>
          {playing ? <Pause weight="fill" className="text-black" size={18} /> : <Play weight="fill" className="text-black" size={18} />}
        </button>
        <div className="flex-1">
          <div className={`flex items-center gap-[2px] h-10 mb-2 cursor-pointer ${decoding ? "opacity-40 animate-pulse" : ""}`} onClick={seek}>
            {bars.map((amp, i) => {
              const on = (i / bars.length) * 100 <= pct;
              const h = Math.max(6, amp * 100);
              return <div key={i} style={{ height: `${h}%`, background: on ? accent : "#3f3f46" }} className="flex-1 min-w-[1px] transition-colors self-center" />;
            })}
          </div>
          <div className="flex justify-between font-mono text-xs text-muted-foreground">
            <span>{decoding ? "decoding waveform…" : fmt(cur)}</span>
            <span>{fmt(dur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
